import React from 'react';
import type { CircularProgressProps as MuiCircularProgressProps } from '@mui/material';
import { CircularProgress as MuiCircularProgress } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/
export interface CircularProgressProps {
  /** The size of the circle. A number is treated as pixels; a string must include a CSS unit (e.g. '3rem'). */
  size?: number | string;
  /** CSS class name applied to the root element. */
  className?: string;
  /** Inline styles applied to the root element. */
  style?: React.CSSProperties;
  /** Override or extend the styles applied to the component. */
  classes?: MuiCircularProgressProps['classes'];
  /** Test id forwarded to the root element. */
  'data-testid'?: string;
}

const CircularProgress: React.VFC<CircularProgressProps> = ({
  size,
  className,
  style,
  classes,
  'data-testid': dataTestId,
  ...restProps
}) => {
  if (!isEmpty(restProps)) {
    reportErrorInProductionOrThrow(
      `Unexpected props passed to CircularProgress: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the CircularProgressProps interface and to the component's signature.`,
      true
    );
  }

  // data-testid is not part of @mui/material's typed props under @types/react v17, so it is
  // forwarded via a spread to avoid an excess-property error while still reaching the root element.
  const dataTestIdProp = dataTestId === undefined ? {} : { 'data-testid': dataTestId };

  return (
    <MuiCircularProgress
      size={size}
      className={className}
      style={style}
      classes={classes}
      {...dataTestIdProp}
      {...restProps}
    />
  );
};

export default CircularProgress;
